import React, { Fragment, useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import Table from "react-bootstrap/Table";
import { PaginationControl } from "react-bootstrap-pagination-control";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { AxisDateTimeFull } from "../util/AxisDateTime";
import AxisSpinner from "../components/AxisSpinner";
import DropDownUserRoleSelection from "../components/DropDownUserRoleSelection";
import { UserLink } from "../util/Links";
import { ButtonNewUser } from "../components/ShiftShiftButtonActionsElements";
import UserCreateUpdateModal from "../modals/UserCreateUpdateModal";
import Button from "react-bootstrap/Button";
import { PageTitleController } from "../components/PageTitleController";
import { BsCircleFill, BsPeopleFill } from "react-icons/bs";
import { Container } from "react-bootstrap";
import { useLocation } from "react-router-dom";

const UsersTable = () => {
  let { state } = useLocation();
  const getKeyStartingState = () => {
    if (state === null || state === undefined) return "ACTIVE";
    if (state.hasOwnProperty("redirectKey")) return state.redirectKey;
    return "ACTIVE";
  };

  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [offset, setOffset] = useState(0);
  const [totalShifts, setTotalShifts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [fetchCount, setFetchCount] = useState(25);
  const [key, setKey] = useState(getKeyStartingState());
  const [role, setRole] = useState(null);
  const [sortKey, setSortKey] = useState("surname");
  const [sortDirection, setSortDirection] = useState("asc");
  const [showNewUser, setShowNewUser] = useState(false);
  const searchRef = useRef();

  useEffect(() => {
    document.title = "Users";
  }, []);

  useEffect(() => {
    // Clear dataset when changing tabs to prevent showing stale data
    setDataSource([]);
    setTotalShifts(0);
    setTotalPages(1);
    setOffset(0);
    paginate();
  }, [key, role, sortKey, sortDirection]);

  useEffect(() => {
    paginate();
  }, [offset]);

  const paginate = async () => {
    try {
      setLoading(true);

      var params = {};
      if (key !== "all") {
        params["status"] = key;
      }
      if (role !== null) {
        params["role"] = role;
      }
      if (searchRef.current.value !== null && searchRef.current.value !== "") {
        params["search"] = searchRef.current.value;
      }

      const pageSettings = {
        direction: sortDirection,
        key: sortKey,
        page: offset,
        elementPerPage: fetchCount,
      };
      const config = {
        headers: {
          "content-type": "application/json",
        },
        params: params,
      };

      const page = await authAxios().post(
        "/api/backoffice/users/paging",
        pageSettings,
        config,
      );
      setLoading(false);

      if (page.data.content.length === 0) {
        if (dataSource.length === 0) {
          setTotalShifts(0);
        }
        console.log("empty api call");
        setDataSource([]);
        return;
      }

      setDataSource(page.data.content);
      setTotalShifts(page.data.totalElements);
      var length = Math.ceil(page.data.totalElements / fetchCount);
      setTotalPages(length);
    } catch (error) {
      setLoading(false);
      // Clear data on error to prevent showing stale results
      setDataSource([]);
      setTotalShifts(0);
      setTotalPages(1);
      console.log("Error catch");
      console.log(error);
      console.log(error.stack);
    }
  };

  const handleSetSortKey = (key) => {
    if (sortKey === key) {
      if (sortDirection === "asc") {
        setSortDirection("dsc");
      } else {
        setSortDirection("asc");
      }
      return;
    }
    setSortKey(key);
  };

  const tableHeading = (key, name) => {
    if (key === sortKey) {
      return sortDirection === "dsc" ? name + " ↓" : name + " ↑";
    }
    return name;
  };

  const SpinnerOrData = (loading) => {
    if (loading) return <AxisSpinner loading={loading} />;

    return (
      <Fragment>
        <Table striped bordered hover>
          <thead>
            <tr style={{ cursor: "pointer" }}>
              <th onClick={() => handleSetSortKey("surname")}>
                {tableHeading("surname", "Name")}
              </th>
              <th onClick={() => handleSetSortKey("email")}>
                {tableHeading("email", "Email")}
              </th>
              <th onClick={() => handleSetSortKey("telephone")}>
                {tableHeading("telephone", "Telephone")}
              </th>
              <th>App Version</th>
              {key === "HOLD" && (
                <>
                  <th>Hold Reason</th>
                  <th>Hold Date</th>
                </>
              )}
              <th onClick={() => handleSetSortKey("role")}>
                {tableHeading("role", "Role")}
              </th>
              <th onClick={() => handleSetSortKey("status")}>
                {tableHeading("status", "Status")}
              </th>
              <th onClick={() => handleSetSortKey("created")}>
                {tableHeading("created", "Created")}
              </th>
            </tr>
          </thead>
          <tbody>
            {dataSource.map((user) => (
              <tr key={user?.id}>
                <td>{UserLink(user)}</td>
                <td>{user?.email}</td>
                <td>{user?.telephone}</td>
                <td>{user?.appVersion}</td>
                {key === "HOLD" && (
                  <>
                    <td>{user?.onHoldReason || '-'}</td>
                    <td>{user?.onHoldDate ? AxisDateTimeFull(user?.onHoldDate) : '-'}</td>
                  </>
                )}
                <td className={"text-capitalize"}>
                  {user?.role.toLowerCase()}
                </td>
                <td className={"text-capitalize icon-text-wrapper"}>
                  <BsCircleFill
                    className={
                      "icon-right-spacing " +
                      user?.status.toLowerCase() +
                      "-icon-colour"
                    }
                  />
                  {user?.status.toLowerCase()}
                </td>
                <td>{AxisDateTimeFull(user?.created)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        <div>
          <p>
            Page {offset + 1} of {totalPages}, showing {fetchCount} records(s)
            out of {totalShifts}
          </p>
          <div>
            <PaginationControl
              page={offset + 1}
              between={3}
              total={totalShifts}
              limit={fetchCount}
              changePage={(page) => {
                setOffset(page - 1);
              }}
              ellipsis={1}
            />
          </div>
        </div>
      </Fragment>
    );
  };

  function roleSelected(role) {
    setRole(role);
  }

  return (
    <div>
      <Row className={"icon-text-wrapper"}>
        <PageTitleController
          title={"Users"}
          icon={<BsPeopleFill className={"icon-right-spacing"} />}
        />
        <Col>
          <Row xs={"auto"} className={"justify-content-lg-end"}>
            <ButtonNewUser action={() => setShowNewUser(true)} />
          </Row>
        </Col>
      </Row>

      <Tabs
        id="shifts-table-tabs"
        activeKey={key}
        onSelect={(k) => setKey(k)}
        className="mb-3"
      >
        <Tab eventKey="ACTIVE" title="Active"></Tab>
        <Tab eventKey="HOLD" title="Hold"></Tab>
        <Tab eventKey="PENDING" title="Pending"></Tab>
        <Tab eventKey="BLOCKED" title="Blocked"></Tab>
        <Tab eventKey="all" title="All"></Tab>
      </Tabs>
      <Container fluid className={"p-0 m-0"}>
        <Row xs="auto" className={"align-items-center g-3"}>
          <Col xs={"auto"}>
            <input
              type="text"
              placeholder="search"
              ref={searchRef}
              className={"fs-input btn-w-px axis-input"}
            />
          </Col>
          <Col xs={"auto"}>
            <DropDownUserRoleSelection 
              onSelected={roleSelected}
              placeholderText="Select Role" 
            />
          </Col>
          <Col xs={"auto"}>
            <Button variant={"outline-offblack"} onClick={() => paginate()}>
              Filter
            </Button>
          </Col>
        </Row>
      </Container>

      <br />
      {SpinnerOrData(loading)}

      <UserCreateUpdateModal
        visible={showNewUser}
        close={() => setShowNewUser(false)}
      />
    </div>
  );
};

export default UsersTable;
